"use client";

import React from "react";
import { Award, BookOpen, CircleCheck as CheckCircle2, GitCompare, SearchCheck, Sparkles, ArrowRight, ShieldCheck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
interface EditorialFormatItem {
  id: string;
  title: string;
  badge: string;
  description: string;
  criteria: string;
  iconName: "award" | "book" | "search" | "compare" | "sparkles";
  linkUrl: string;
  buttonLabel: string;
}
const editorialFormats: EditorialFormatItem[] = [{
  id: "best-products",
  title: "Best products",
  badge: "Tested roundups",
  description: "Comprehensive, categorized shortlists ranking top-performing pet foods, beds, and health essentials based on clinical trials and pet trials.",
  criteria: "Veterinary-reviewed scoring across safety, durability, and real nutritional or physical benefits.",
  iconName: "award",
  linkUrl: "/ProductsReviewsPage",
  buttonLabel: "See products"
}, {
  id: "buying-guides",
  title: "Buying guides",
  badge: "Selection criteria",
  description: "In-depth educational frameworks that walk you through ingredient safety, material standards, and size-specific pet gear requirements.",
  criteria: "Step-by-step checklists to help you evaluate products before making any purchase.",
  iconName: "book",
  linkUrl: "/ProductsReviewsPage",
  buttonLabel: "See guides"
}, {
  id: "product-reviews",
  title: "Product reviews",
  badge: "Hands-on analysis",
  description: "Rigorous single-item teardowns highlighting real-world performance, veterinary safety assessments, pros, and clear drawbacks.",
  criteria: "Unbiased 30-day trial observations under standard household conditions.",
  iconName: "search",
  linkUrl: "/ProductsReviewsPage",
  buttonLabel: "See reviews"
}, {
  id: "comparisons",
  title: "Comparisons",
  badge: "Head-to-head",
  description: "Direct side-by-side breakdowns contrasting two or three leading brands across formula purity, pricing, and suitability.",
  criteria: "Direct metric matching to show exactly where one formula or device outperforms another.",
  iconName: "compare",
  linkUrl: "/ProductsReviewsPage",
  buttonLabel: "See comparisons"
}, {
  id: "editors-picks",
  title: "Editor's picks",
  badge: "Staff favorites",
  description: "Curated recommendations verified by our editorial board and consulting veterinarians for outstanding long-term value and pet wellbeing.",
  criteria: "Consistently positive veterinary feedback and documented owner satisfaction.",
  iconName: "sparkles",
  linkUrl: "/ProductsReviewsPage",
  buttonLabel: "See picks"
}];
export default function EditorialFormats() {
  const shouldReduceMotion = useReducedMotion();
  const getFormatIcon = (name: EditorialFormatItem["iconName"]) => {
    switch (name) {
      case "award":
        return <Award className="size-5 text-accent" />;
      case "book":
        return <BookOpen className="size-5 text-accent" />;
      case "search":
        return <SearchCheck className="size-5 text-accent" />;
      case "compare":
        return <GitCompare className="size-5 text-accent" />;
      case "sparkles":
        return <Sparkles className="size-5 text-accent" />;
      default:
        return <Award className="size-5 text-accent" />;
    }
  };
  return <section id="editorial-formats" data-nav="light" className="w-full bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-muted border border-border text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-4">
            <ShieldCheck className="size-3.5 text-accent" />
            <span>Clinical review standards</span>
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-foreground mb-4">
            How we review and evaluate pet products
          </h2>

          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            Every pet product guide on PetsWhirl follows a structured, evidence-informed testing framework. Explore our five core editorial review formats designed to give you clarity and complete buying confidence.
          </p>
        </div>

        {/* Formats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {editorialFormats.map((item, index) => {
          const isFeatured = index === 0;
          return <motion.div key={item.id} data-index={index} initial={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 16
          }} whileInView={shouldReduceMotion ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.15
          }} transition={{
            duration: 0.45,
            delay: shouldReduceMotion ? 0 : index * 0.08,
            ease: [0.16, 1, 0.3, 1]
          }} className={isFeatured ? "md:col-span-2 lg:col-span-2" : "col-span-1"}>
                <Card className="h-full flex flex-col justify-between rounded-xl border border-border bg-card shadow-xs hover:shadow-md transition-[border-color,box-shadow] duration-200">
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between gap-4 mb-3">
                      <div className="size-11 rounded-lg bg-background border border-border flex items-center justify-center">
                        {getFormatIcon(item.iconName)}
                      </div>
                      <span className="text-xs font-medium px-2.5 py-1 rounded-md bg-secondary text-secondary-foreground border border-border">
                        {item.badge}
                      </span>
                    </div>
                    <CardTitle className="font-serif text-xl sm:text-2xl text-card-foreground">
                      {item.title}
                    </CardTitle>
                    <CardDescription className="text-muted-foreground text-sm sm:text-base leading-relaxed pt-1">
                      {item.description}
                    </CardDescription>
                  </CardHeader>

                  <CardContent className="pt-0 flex-1">
                    <div className="p-3.5 rounded-lg bg-background/80 border-l-2 border-l-accent border border-border">
                      <div className="flex items-start gap-2">
                        <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" />
                        <p className="text-xs sm:text-sm text-foreground/90 leading-normal">
                          <strong className="font-semibold text-foreground">Standard:</strong> {item.criteria}
                        </p>
                      </div>
                    </div>
                  </CardContent>

                  <CardFooter className="pt-2 border-t border-border/60 flex items-center justify-between">
                    <Button asChild variant="default" size="sm" className="w-full sm:w-auto font-medium">
                      <Link to={item.linkUrl}>
                        <span>{item.buttonLabel}</span>
                        <ArrowRight className="size-4" />
                      </Link>
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </div>
      </div>
    </section>;
}